import {
  getContentVersionHistoryUrl,
  getDownloadUrl,
  getFileContentUrl,
  getFilesUrl,
} from "../config/Config";
import downloadFile from "../utils/Dom";
import { zipFiles } from "../utils/FileUtils";
import { deleteRequest, getRequest, postRequest } from "./ServiceHelper";

export const FileType = { ASSESSMENT_GUIDES: "assessment-guides" };

export class Files {
  /**
   * Returns pre-signed URL and corresponding fields to upload to S3
   * @param fileName
   * @param type
   * @return {Promise<*>}
   */
  static getPreSignedPostUrl = (
    fileName,
    type,
    path = null,
    createTask = false,
    metadata = null
  ) => {
    const params = {};
    params.type = type;
    if (path) {
      params.path = path;
    }
    if (createTask) {
      params.createTask = createTask;
    }
    if (metadata) {
      params.metadata = JSON.stringify(metadata);
    }
    return getRequest(getFilesUrl() + "/" + fileName, params);
  };

  static getPreSignedGetUrl = (
    fileName,
    type,
    path = null,
    createTask = false
  ) => {
    const params = {};
    params.type = type;
    if (path) {
      params.path = path;
    }
    if (createTask) {
      params.createTask = createTask;
    }
    params.urlType = "get";
    return getRequest(getFilesUrl() + "/" + fileName, params);
  };

  static async saveFile(
    file,
    type,
    path = null,
    createTask = false,
    metadata = null
  ) {
    const preSignedUrl = await this.getPreSignedPostUrl(
      file.name,
      type,
      path,
      createTask,
      metadata
    );
    let taskId = null;
    if (preSignedUrl.fields["x-amz-meta-taskId"]) {
      taskId = preSignedUrl.fields["x-amz-meta-taskId"];
    }
    const formData = new FormData();
    for (const [key, value] of Object.entries(preSignedUrl.fields)) {
      formData.append(key, value);
    }
    formData.append("File", file);
    const response = postRequest(
      preSignedUrl.url,
      formData,
      false,
      false,
      true
    );
    return { response, taskId };
  }

  static async getFileWithPresignedGetUrl(fileName, type, path) {
    const preSignedUrl = await this.getPreSignedGetUrl(fileName, type, path);
    const response = await fetch(preSignedUrl, { method: "GET" });
    // extract the content
    return response.json();
  }

  static postPreSignedUrl(preSignedUrl, fields, file) {
    const formData = new FormData();
    for (const [key, value] of Object.entries(fields)) {
      formData.append(key, value);
    }
    formData.append("File", file);
    return postRequest(preSignedUrl, formData, false, false, true);
  }

  static async reviewFiles(type, path = null, filter = null) {
    const paths = await Files.requestDownload(type, path, filter);
    return paths;
  }

  /**
   * Downloads all files in a given path
   * @param {string} type  A Type conforming to DDL File Types
   * @param {string} path Any dirs following the type
   */
  static downloadFiles = async (type, path) => {
    const paths = await Files.requestDownload(type, path);
    paths.forEach((element) => {
      downloadFile(element.file_url, element.file_name);
    });
  };

  /**
   * Requests a list pre-signed URLs to download a collection of files from S3
   * @param {string} type A Type conforming to DDL File Types
   * @param {string} path Any dirs following the type
   * @param {Object} filter Any additional filters
   * @returns
   */
  static requestDownload = (type, path, filter = null) => {
    let params = {};
    params.type = type;
    if (path) {
      params.path = path;
    }
    if (filter) {
      params = { ...params, ...filter };
    }
    return getRequest(getDownloadUrl(), params);
  };

  /**
   * Zips two or more files using DEFLATE with maximum compression
   * @param {Array} files Array of files to zip
   * @param {string} type A Type conforming to DDL File Types
   * @param {string} path Any dirs following the type
   * @param {string} fileName For Zipped object, the name of the file
   * @returns {Promise<Blob>}
   */
  static async saveFiles(
    files,
    type,
    path = null,
    fileName = null,
    zip = true
  ) {
    let file;
    if (zip) {
      if (Array.isArray(files) && files.length > 1) {
        file = await zipFiles(files);
        file = new File([file], fileName, { type: "application/zip" });
      } else {
        file = files[0];
      }
      await Files.saveFile(file, type, path);
    } else {
      const filePromises = files.map((file) => {
        return Files.saveFile(file, type, path);
      });
      await Promise.all(filePromises);
    }
  }

  /**
   * Returns the contents of a file given a path
   * @param {*} path
   * @returns
   */
  static getFileContent(fileName, type, path) {
    return getRequest(getFileContentUrl() + "/" + fileName, {
      type: type,
      path: path,
    });
  }

  static getPublicFileContent(fileName, type, path, tenantId) {
    return getRequest(getFileContentUrl() + "/public/" + fileName, {
      type: type,
      path: path,
      tenantId: tenantId,
    });
  }

  static getPublicFiles(type, path, tenantId) {
    return getRequest(getFileContentUrl() + "/public", {
      type: type,
      path: path,
      tenantId: tenantId,
    });
  }

  static deleteFile(fileName, type, path = null, metadata = null) {
    const params = {};
    params.type = type;
    if (path) {
      params.path = path;
    }
    if (metadata) {
      params.metadata = JSON.stringify(metadata);
    }
    return deleteRequest(getFilesUrl() + "/" + fileName, params);
  }

  static getContentVersionHistory = (fileType, contextPath) => {
    return getRequest(getContentVersionHistoryUrl(fileType), { contextPath });
  };
}
